import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api, uploadUrl } from '../api/client.js';

const FALLBACK = {
  school: 'https://picsum.photos/seed/exoticpet-school/600/400',
  expo: 'https://picsum.photos/seed/exoticpet-expo/600/400',
  contest: 'https://picsum.photos/seed/exoticpet-contest/600/400',
  other: 'https://picsum.photos/seed/exoticpet/600/400',
};
const HEIGHTS = ['h-48', 'h-64', 'h-56', 'h-72', 'h-48', 'h-60'];
const FILTERS = [['Semua', 'all'], ['School', 'school'], ['Expo', 'expo'], ['Contest', 'contest'], ['Lainnya', 'other']];

export default function PortfolioSection({ limit = 12, showViewAll = true }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [displayModal, setDisplayModal] = useState(null);
  const [modalClosing, setModalClosing] = useState(false);
  const modalCloseTimeoutRef = useRef(null);

  useEffect(() => {
    if (modal) {
      if (modalCloseTimeoutRef.current) clearTimeout(modalCloseTimeoutRef.current);
      setModalClosing(false);
      setDisplayModal(modal);
    } else if (displayModal) {
      setModalClosing(true);
      modalCloseTimeoutRef.current = setTimeout(() => {
        setDisplayModal(null);
        setModalClosing(false);
      }, 280);
    }
    return () => { if (modalCloseTimeoutRef.current) clearTimeout(modalCloseTimeoutRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  useEffect(() => {
    document.body.classList.toggle('modal-open', !!displayModal);
    return () => document.body.classList.remove('modal-open');
  }, [displayModal]);

  useEffect(() => {
    api.get(`/portfolio?limit=${limit}`).then(({ data }) => setItems(data.items || []));
  }, [limit]);

  const filtered = useMemo(() => (filter === 'all' ? items : items.filter((i) => i.kategori === filter)), [items, filter]);

  return (
    <section id="portfolio" className="py-20 md:py-28" style={{ background: 'linear-gradient(180deg, transparent, color-mix(in oklab, var(--accent) 4%, transparent), transparent)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-4 border" style={{ background: 'color-mix(in oklab, var(--accent) 8%, transparent)', borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
            <span className="material-symbols-outlined">photo_library</span> PORTOFOLIO
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4">
            Portofolio <span style={{ color: 'var(--accent)' }}>Karya</span> Kami
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 max-w-xl mx-auto">
            Ratusan event sukses telah kami selenggarakan di berbagai institusi di Surabaya dan sekitarnya.
          </p>
        </div>

        <div className="flex md:flex-wrap md:justify-center gap-2.5 mb-10 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible">
          {FILTERS.map(([label, val]) => {
            const active = filter === val;
            return (
              <button
                key={val}
                onClick={() => setFilter(val)}
                className={`flex-shrink-0 px-5 py-2.5 rounded-[18px] text-sm font-semibold border transition-all duration-300 ease-out hover:rounded-[12px] active:rounded-[24px] active:scale-[0.96] ${
                  active
                    ? 'text-white shadow-md border-transparent'
                    : 'bg-white dark:bg-neutral-800 text-forest-600 dark:text-forest-400 border-forest-500/30 hover:bg-[var(--accent-pill-bg)] hover:border-transparent hover:text-[var(--accent-active-text)] dark:hover:bg-forest-500/10 dark:hover:text-forest-300'
                }`}
                style={active ? { background: 'var(--accent)' } : undefined}
              >
                {label}
              </button>
            );
          })}
        </div>

        {filtered.length > 0 ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
            {filtered.map((item, i) => {
              const img = item.image ? uploadUrl(item.image) : FALLBACK[item.kategori] || FALLBACK.other;
              const h = HEIGHTS[i % HEIGHTS.length];
              const desc = item.deskripsi || `Kegiatan ${item.judul} yang diselenggarakan oleh tim profesional kami dengan hasil memuaskan.`;
              return (
                <div key={item.id} onClick={() => setModal({ ...item, img, desc })} className="break-inside-avoid mb-5 group relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer">
                  <img src={img} alt={item.judul} loading="lazy" className={`w-full ${h} object-cover transition-transform duration-500 group-hover:scale-110`} onError={(e) => { e.target.onerror = null; e.target.src = 'https://picsum.photos/seed/exoticpet/800/600'; }} />
                  <div className="absolute inset-0 flex flex-col justify-end p-5 opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ background: 'linear-gradient(transparent, rgba(10,30,10,0.85))' }}>
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-2 self-start capitalize" style={{ background: 'color-mix(in oklab, var(--accent) 80%, transparent)' }}>{item.kategori}</span>
                    <h4 className="text-white font-bold text-sm">{item.judul}</h4>
                    {item.konten_artikel && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold mt-2 self-start px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,199,44,0.9)', color: '#1a2e0a' }}>
                        <span className="material-symbols-outlined text-sm">article</span> Baca Artikel
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ background: 'color-mix(in oklab, var(--accent) 10%, transparent)' }}>
              <span className="material-symbols-outlined text-4xl" style={{ color: 'var(--accent)' }}>photo_library</span>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">Belum Ada Portofolio</h3>
            <p className="text-neutral-500 dark:text-neutral-400 max-w-md mx-auto">Belum ada portofolio yang ditampilkan saat ini.</p>
          </div>
        )}

        {showViewAll && (
          <div className="text-center mt-10">
            <Link to="/portofolio" className="inline-flex items-center gap-2 px-8 py-4 rounded-3xl font-bold text-white shadow-md hover:rounded-xl active:scale-[0.98] transition-all duration-300" style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))' }}>
              <span className="material-symbols-outlined">grid_view</span> Lihat Semua Portofolio
            </Link>
          </div>
        )}
      </div>

      {displayModal && createPortal(
        <div
          className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 transition-opacity duration-[280ms] ${modalClosing ? 'opacity-0' : 'opacity-100'}`}
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
          onClick={() => setModal(null)}
        >
          <div
            className={`relative max-w-4xl w-full bg-white dark:bg-neutral-900 rounded-3xl overflow-hidden shadow-2xl ${modalClosing ? 'portfolio-modal-out' : 'portfolio-modal-in'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 hover:rounded-2xl active:rounded-3xl active:scale-95 text-white flex items-center justify-center transition-all duration-300">
              <span className="material-symbols-outlined text-2xl">close</span>
            </button>
            <div className="flex flex-col md:flex-row">
              <div className="md:w-3/5 bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center p-4">
                <img src={displayModal.img} alt="Preview" className="max-w-full max-h-[50vh] md:max-h-[70vh] object-contain rounded-xl" onError={(e) => { e.target.onerror = null; e.target.src = 'https://picsum.photos/seed/exoticpet/800/600'; }} />
              </div>
              <div className="md:w-2/5 p-6 md:p-8 flex flex-col">
                <span className="inline-block w-fit px-3 py-1 rounded-full text-xs font-bold uppercase mb-4" style={{ background: 'color-mix(in oklab, var(--accent) 15%, transparent)', color: 'var(--accent)' }}>{displayModal.kategori}</span>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white mb-4">{displayModal.judul}</h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed text-sm md:text-base flex-1">{displayModal.desc}</p>
                {displayModal.konten_artikel && (
                  <Link to={`/artikel/${displayModal.id}`} className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-semibold text-center hover:shadow-lg hover:rounded-2xl active:rounded-3xl active:scale-[0.98] transition-all duration-300" style={{ background: 'linear-gradient(135deg, #FFC72C, #E6A000)', color: '#1a2e0a' }}>
                    <span className="material-symbols-outlined text-lg">article</span> Baca Artikel
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
